import type { ComponentProps, JSX } from 'preact';

import type { UnpackSignals } from '@oracle/oraclejet-internal-utilities/attributeUtils';
import { merge } from '@oracle/oraclejet-internal-utilities/stringUtils';
import type { Label } from '#UNSAFE_Label';
import type { InlineUserAssistance, UserAssistanceDensityType } from '#UNSAFE_UserAssistance';
import { useId } from '#hooks/UNSAFE_useId';
import { useFormFieldContextProps } from '#hooks/UNSAFE_useFormFieldContextProps';
import { useMessageSeverity } from '#hooks/PRIVATE_useMessageSeverity';
import { Switch } from './Switch';

type InlineUserAssistanceProps = ComponentProps<typeof InlineUserAssistance>;
type PickedUserAssistanceProps = Pick<InlineUserAssistanceProps, 'messages'>;
type SwitchProps = ComponentProps<typeof Switch>;

type UseSwitchProps = PickedUserAssistanceProps & {
  ariaDescribedBy?: SwitchProps['aria-describedby'];
  helpSourceLink?: SwitchProps['helpSourceLink'];
  helpSourceText?: SwitchProps['helpSourceText'];
  isDisabled?: SwitchProps['isDisabled'];
  isReadonly?: SwitchProps['isReadonly'];
  label: SwitchProps['label'];
  labelEdge?: SwitchProps['labelEdge'];
  userAssistanceDensity?: UserAssistanceDensityType;
  value?: SwitchProps['value'];
};

/**
 * A custom hook to determine the props for a component that renders
 * a Switch. This is similar to useTextField, but unlike TextField
 * Switch does not render truncated labels nor does it render required.
 */
export function useSwitch(props: UseSwitchProps) {
  const {
    ariaDescribedBy,
    helpSourceLink,
    helpSourceText,
    isDisabled,
    isReadonly,
    label,
    labelEdge,
    messages,
    userAssistanceDensity,
    value
  } = props;
  const baseId = useId();
  const labelId = labelEdge !== 'none' ? `${baseId}-label` : undefined;
  const uaId = !isDisabled ? `${baseId}-ua` : undefined;

  const hasValue = value ?? false;

  // Create the form field context (no truncation/required logic needed)
  const formFieldContext = useFormFieldContextProps({
    hasValue,
    isDisabled,
    isReadonly
  });

  // Determine the highest severity from the messages
  const messageSeverity = useMessageSeverity(messages);

  // Determine the label variant
  const labelVariant: ComponentProps<typeof Label>['variant'] =
    labelEdge !== 'none' ? labelEdge : undefined;

  const ariaLabel = label && label.length > 0 && labelEdge === 'none' ? label : undefined;
  const ariaLabelledBy = ariaLabel ? undefined : labelId;

  // According to UX, Switch's compact user assistance density behaves like efficient.
  const resolvedUserAssistanceDensity =
    userAssistanceDensity === 'compact' ? 'efficient' : userAssistanceDensity;

  return {
    baseId,
    formFieldContext,
    styledSwitchProps: {
      'aria-describedby': merge([uaId, ariaDescribedBy]),
      'aria-invalid':
        messageSeverity === 'error'
          ? 'true'
          : (undefined as UnpackSignals<JSX.AriaAttributes>['aria-invalid']),
      ...(ariaLabelledBy && { 'aria-labelledby': ariaLabelledBy }),
      ...(ariaLabel && { 'aria-label': ariaLabel })
    },
    labelProps: {
      helpSourceLink,
      helpSourceText,
      id: labelId,
      userAssistanceDensity: resolvedUserAssistanceDensity,
      variant: labelVariant
    },
    userAssistanceProps: {
      id: uaId
    }
  };
}
